import React, { useContext, useRef, useState } from "react";
import { format } from "date-fns";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getByPayoutsCaseById,
  updatePayoutCase,
} from "../../../reducers/payouts.slice";
import { convertDateToCT } from "../../../utils/util";
import Slider from "react-slick";
import { ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, Copy, MessageSquare } from "lucide-react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { AuthContext } from "../../../context/authContext";
import PageHeader from "../../../components/shared/PageHeader";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectOption } from "../../../components/ui/select";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const formatTimestamp = (timestamp) => {
  try {
    console.log("timestamp", timestamp);
    return convertDateToCT(timestamp, "MMMM D, YYYY hh:mm:ss A");
  } catch (e) {
    return timestamp;
  }
};

const PayoutCasesPages = ({ dialogMode = false, row: rowProp, onSuccess }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const row = rowProp || location?.state?.row;
  const [copiedField, setCopiedField] = useState(null);
  const [slideIndexes, setSlideIndexes] = useState({});
  const sliderRefs = useRef({});
  const [discrepancyOpen, setDiscrepancyOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [allNotes, setAllNotes] = useState(row?.notes || []);
  const [showLoader, setShowLoader] = useState(false);
  const discripency = row?.discrepanies?.discrepancy_list;

  const [formData, setFormData] = useState({
    case_number: "" || row?.case_number,
    status: "" || row?.status,
    notes: "",
  });

  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const notesPerPage = 3;
  const totalPages = Math.ceil(allNotes.length / notesPerPage);

  const getCurrentNotes = () => {
    const startIndex = (page - 1) * notesPerPage;
    const endIndex = startIndex + notesPerPage;
    return allNotes?.slice(startIndex, endIndex);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowLoader(true);
    const payload = {
      id: row?.id,
      ...formData,
    };
    dispatch(updatePayoutCase(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setShowLoader(false);
      if (dialogMode && onSuccess) onSuccess();
      else navigate(-1);
    });
  };

  const handleFormData = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const statusType = ["UNDER_INVESTIGATION", "RESOLVED"];

  const handleStatusChange = (e) => {
    setFormData((prev) => ({ ...prev, status: e.target.value }));
  };

  const handleCopy = (value) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedField(value);
        setTimeout(() => setCopiedField(null), 2000);
      })
      .catch((err) => console.error("Failed to copy: ", err));
  };

  const next = (id) => {
    if (sliderRefs.current[id]) {
      sliderRefs.current[id].slickNext();
    }
  };

  const previous = (id) => {
    if (sliderRefs.current[id]) {
      sliderRefs.current[id].slickPrev();
    }
  };

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    afterChange: (current) => {
      setSlideIndexes((prev) => ({ ...prev, discrepancy: current }));
    },
  };

  const fieldBox = {
    padding: "10px 14px",
    borderRadius: 8,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
  };

  return (
    <div style={{ maxWidth: dialogMode ? 900 : 600, width: "100%", margin: "0 auto", paddingBottom: dialogMode ? 0 : 40 }}>
      {!dialogMode && (
        <>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "transparent",
              border: "none",
              color: "rgba(255,255,255,0.6)",
              fontSize: 13,
              cursor: "pointer",
              padding: 0,
              marginBottom: 20,
            }}
          >
            <ArrowLeft size={15} /> Back
          </button>

          <PageHeader title="Payout Case" subtitle={row?.case_number ? `Case #${row.case_number}` : ""} />
        </>
      )}

      {/* Discrepancy collapsible */}
      <div
        style={{
          marginTop: 20,
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          onClick={() => setDiscrepancyOpen((o) => !o)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            background: "rgba(255,255,255,0.03)",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Discrepancy Details
          <ChevronDown
            size={16}
            style={{
              transform: discrepancyOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </button>

        {discrepancyOpen && (
          <div style={{ padding: "16px 16px 20px" }}>
            <Slider
              {...settings}
              ref={(slider) => {
                sliderRefs.current["discrepancy"] = slider;
              }}
            >
              {discripency?.length > 0 ? (
                discripency.map((item, index) => (
                  <div key={index}>
                    <div
                      style={{
                        padding: 20,
                        borderRadius: 10,
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#00ADB5",
                          marginBottom: 14,
                        }}
                      >
                        Item {index + 1}
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                          gap: 12,
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {[
                            { label: "UPC", value: item.upc },
                            { label: "GTIN", value: item.gtin },
                            { label: "SKU", value: item.sku },
                          ].map(({ label, value }) => (
                            <div key={label} style={fieldBox}>
                              <div
                                style={{
                                  fontSize: 10,
                                  color: "#8b9296",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                  marginBottom: 4,
                                }}
                              >
                                {label}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 6,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 13,
                                    color: "#fff",
                                    fontWeight: 500,
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {value}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleCopy(value)}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 2,
                                    color:
                                      copiedField === value
                                        ? "#4caf50"
                                        : "#ffffff85",
                                    flexShrink: 0,
                                  }}
                                >
                                  <Copy size={13} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div
                          style={{
                            ...fieldBox,
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#00ADB5",
                              marginBottom: 4,
                            }}
                          >
                            Details
                          </div>
                          {[
                            {
                              label: "Shipped Quantity",
                              value: item?.shipped_quantity,
                            },
                          ].map(({ label, value }) => (
                            <div
                              key={label}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span style={{ fontSize: 11.5, color: "#8b9296" }}>
                                {label}:
                              </span>
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: "#fff",
                                }}
                              >
                                {value}
                              </span>
                            </div>
                          ))}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ fontSize: 11.5, color: "#8b9296" }}>
                              Fill Rate:
                            </span>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 500,
                                color:
                                  parseFloat(item.fill_rate) < 70
                                    ? "#f44336"
                                    : parseFloat(item.fill_rate) < 90
                                    ? "#ff9800"
                                    : "#4caf50",
                              }}
                            >
                              {item.fill_rate}%
                            </span>
                          </div>
                          <div
                            style={{
                              width: "100%",
                              height: 6,
                              borderRadius: 4,
                              background: "rgba(255,255,255,0.1)",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(parseFloat(item.fill_rate || 0), 100)}%`,
                                height: "100%",
                                background:
                                  parseFloat(item.fill_rate) < 70
                                    ? "#f44336"
                                    : parseFloat(item.fill_rate) < 90
                                    ? "#ff9800"
                                    : "#4caf50",
                                transition: "width 1s ease-in-out",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "32px 0",
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: 10,
                  }}
                >
                  <span style={{ fontSize: 13.5, color: "#8b9296" }}>
                    No discrepancy data available for this case
                  </span>
                </div>
              )}
            </Slider>

            {discripency?.length > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  marginTop: 12,
                }}
              >
                <button
                  type="button"
                  onClick={() => previous("discrepancy")}
                  disabled={
                    !slideIndexes["discrepancy"] ||
                    slideIndexes["discrepancy"] === 0
                  }
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8,
                    padding: isMobile ? "10px 14px" : "4px 8px",
                    cursor: "pointer",
                    color: "#fff",
                    opacity:
                      !slideIndexes["discrepancy"] ||
                      slideIndexes["discrepancy"] === 0
                        ? 0.4
                        : 1,
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => next("discrepancy")}
                  disabled={
                    slideIndexes["discrepancy"] === discripency?.length - 1
                  }
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8,
                    padding: isMobile ? "10px 14px" : "4px 8px",
                    cursor: "pointer",
                    color: "#fff",
                    opacity:
                      slideIndexes["discrepancy"] === discripency?.length - 1
                        ? 0.4
                        : 1,
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Update form */}
      <form onSubmit={handleSubmit} style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <Label htmlFor="case_number">Case Number</Label>
          <Input
            id="case_number"
            type="text"
            name="case_number"
            value={formData.case_number}
            placeholder="Enter your case number"
            onChange={handleFormData}
            required
          />
        </div>

        <div>
          <Label htmlFor="status">Status</Label>
          <Select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleStatusChange}
            required
          >
            <SelectOption value="">Please select status</SelectOption>
            {statusType.map((s) => (
              <SelectOption key={s} value={s}>
                {s
                  .split("_")
                  .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
                  .join(" ")}
              </SelectOption>
            ))}
          </Select>
        </div>

        <div>
          <Label htmlFor="notes">Add a Note</Label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            placeholder="Enter your notes"
            onChange={handleFormData}
            rows={4}
            style={{
              width: "100%",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.03)",
              color: "#fff",
              padding: "10px 12px",
              fontSize: 13.5,
              fontFamily: "inherit",
              resize: "vertical",
            }}
          />
        </div>

        <Button type="submit" disabled={showLoader} style={{ width: "100%" }}>
          {showLoader ? "Updating…" : "Update Case"}
        </Button>
      </form>

      {/* Case notes */}
      <div style={{ marginTop: 32 }}>
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#fff",
            marginBottom: 12,
            position: "relative",
            display: "inline-block",
          }}
        >
          Case Notes
          <div
            style={{
              position: "absolute",
              bottom: -8,
              left: 0,
              width: 48,
              height: 3,
              background: "#00ADB5",
              borderRadius: 2,
            }}
          />
        </div>
        <div
          style={{
            height: 1,
            background: "rgba(255,255,255,0.08)",
            marginBottom: 20,
          }}
        />

        {getCurrentNotes().length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {getCurrentNotes().map((note, index) => (
              <div
                key={`note-${page}-${index}`}
                style={{
                  padding: "14px 16px",
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  transition: "transform 0.15s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 11.5,
                    color: "#8b9296",
                    marginBottom: 8,
                  }}
                >
                  <MessageSquare size={12} />
                  {formatTimestamp(note.timestamp)}
                </div>
                <p style={{ fontSize: 13.5, color: "#fff", margin: 0 }}>
                  {note.message}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <span style={{ fontSize: 13.5, color: "#8b9296" }}>
              No notes available for this case
            </span>
          </div>
        )}

        {allNotes?.length > notesPerPage && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              marginTop: 20,
            }}
          >
            <Button
              type="button"
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{ minWidth: 72 }}
            >
              Prev
            </Button>
            <span style={{ fontSize: 13, color: "#c3c8cc" }}>
              {page} / {totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{ minWidth: 72 }}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayoutCasesPages;
